/**
 * Pipelines Page
 *
 * Displays pipeline executions with official EITELKEIT components.
 */

import { useState, useEffect, useCallback } from 'react';
import { tokens, Button, Card, StatDisplay } from '@ekelhaft-tools/eitelkeit';
import { useAgent } from '../hooks/useAgent';
import { useDashboardStore } from '../stores/dashboard';

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export function Pipelines() {
  const { isConnected, listPipelines } = useAgent();
  const { pipelines, setPipelines } = useDashboardStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPipelines = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listPipelines();
      setPipelines(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch pipelines');
    } finally {
      setLoading(false);
    }
  }, [listPipelines, setPipelines]);

  // Fetch pipelines
  useEffect(() => {
    if (!isConnected) {
      setLoading(true);
      return;
    }

    fetchPipelines();
  }, [isConnected, fetchPipelines]);

  if (!isConnected) {
    return (
      <div style={{ padding: tokens.spacing[6] }}>
        <h1 style={{ color: tokens.colors.mint }}>Pipelines</h1>
        <div style={{ color: tokens.colors.textMuted }}>
          Synchronizing with orchestration kernel...
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacing[6],
        padding: tokens.spacing[4],
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ color: tokens.colors.mint, margin: 0 }}>Execution History</h1>
        <Button 
          variant="primary" 
          onClick={fetchPipelines} 
          loading={loading}
          size="md"
          leftIcon={null}
          rightIcon={null}
        >
          Refresh
        </Button>
      </div>

      {/* Stats Summary */}
      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: tokens.spacing[4] }}
      >
        <StatDisplay
          label="Active"
          value={pipelines.filter((p) => p.status === 'running').length.toString()}
          trend={null}
          trendValue={null}
          size="sm"
        />
        <StatDisplay
          label="Succeeded"
          value={pipelines.filter((p) => p.status === 'completed').length.toString()}
          trend={null}
          trendValue={null}
          size="sm"
        />
        <StatDisplay
          label="Failed"
          value={pipelines.filter((p) => p.status === 'failed').length.toString()}
          trend={null}
          trendValue={null}
          size="sm"
        />
        <StatDisplay
          label="Total"
          value={pipelines.length.toString()}
          trend={null}
          trendValue={null}
          size="sm"
        />
      </div>

      {/* Error */}
      {error !== null && (
        <Card variant="glass" padding="md" hover={false}>
          <div style={{ color: tokens.colors.danger }}>{error}</div>
        </Card>
      )}

      {/* Pipeline Executions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[4] }}>
        {pipelines.length === 0 && !loading ? (
          <Card variant="default" padding="md" hover={false}>
            No pipeline executions found.
          </Card>
        ) : (
          pipelines.map((execution) => (
            <Card key={execution.executionId} variant="glass" padding="md" hover={true}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: tokens.colors.mint }}>{execution.pipelineName}</span>
                <span style={{ fontSize: 10, color: tokens.colors.textMuted }}>
                  {execution.executionId.slice(0, 8)}
                </span>
              </div>
              <div style={{ marginTop: tokens.spacing[2], fontSize: 12 }}>
                Status: {execution.status} | Trigger: {execution.trigger}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
